import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Identifiers for the inline glyph set rendered by
 * `<kirigami-paper-section-icon>`. Each one corresponds to a single
 * `@switch` arm in the template and to a single row in the icon registry
 * documented in `docs/feature-paper-sections-menu-redesign/redesign-canvas.html`.
 */
export type PaperSectionIconKey =
  | 'title'
  | 'abstract'
  | 'introduction'
  | 'methods'
  | 'results'
  | 'discussion'
  | 'conclusion'
  | 'references'
  | 'acknowledgments'
  | 'section';

/**
 * Maps an arbitrary section name (English or PT-BR) to one of the
 * registered glyph identifiers. The lookup is case-insensitive and
 * trims surrounding whitespace, falling back to the generic
 * `section` glyph when nothing matches.
 *
 * Exported so unit tests can hit the predicates directly without
 * spinning up a TestBed.
 */
export function resolveIconKey(name: string): PaperSectionIconKey {
  const n = (name ?? '').trim().toLowerCase();
  if (n === 'title') return 'title';
  if (n === 'abstract') return 'abstract';
  if (n === 'introduction' || n === 'intro' || n === 'introdução') return 'introduction';
  if (n.startsWith('method') || n.startsWith('metodolog')) return 'methods';
  if (n.startsWith('subresult') || n.startsWith('result') || n.startsWith('resultado')) {
    return 'results';
  }
  if (n.startsWith('discuss') || n.startsWith('discus')) return 'discussion';
  if (n.startsWith('conclus') || n.startsWith('summary') || n.startsWith('resumo')) {
    return 'conclusion';
  }
  if (n.startsWith('reference') || n.startsWith('biblio') || n.startsWith('referên')) {
    return 'references';
  }
  if (n.startsWith('acknowledg') || n.startsWith('agradec')) return 'acknowledgments';
  return 'section';
}

/**
 * Host-agnostic inline icon used by the report-detail sidebar (and any
 * future paper-section list). Renders a 22×22 SVG glyph picked from the
 * section name via `resolveIconKey`. All glyphs use `currentColor` so
 * the parent row drives hover / active / disabled colours.
 *
 * Inline `@switch` template keeps the component free of
 * `[innerHTML]` / `bypassSecurityTrustHtml` escape hatches.
 *
 * Usage:
 *   <kirigami-paper-section-icon sectionName="Results" />
 */
@Component({
  selector: 'kirigami-paper-section-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: inline-flex;
        width: 22px;
        height: 22px;
        color: inherit;
        line-height: 0;
      }
      svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    `,
  ],
  template: `
    @switch (iconKey()) {
      @case ('title') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <rect x="9" y="3" width="6" height="4" rx="0.5" />
          <rect x="2.5" y="14" width="6" height="4" rx="0.5" />
          <rect x="15.5" y="14" width="6" height="4" rx="0.5" />
          <path d="M12 7v3M12 10H5.5v4M12 10h6v4" />
        </svg>
      }
      @case ('abstract') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="7" x2="14" y2="7" />
          <line x1="3" y1="11" x2="21" y2="11" />
          <line x1="3" y1="15" x2="18" y2="15" />
          <line x1="3" y1="19" x2="12" y2="19" />
        </svg>
      }
      @case ('introduction') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5.5" />
          <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
        </svg>
      }
      @case ('methods') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" />
          <path
            d="M12 2v3M12 19v3M2 12h3M19 12h3M4.6 4.6l2.1 2.1M17.3 17.3l2.1 2.1M4.6 19.4l2.1-2.1M17.3 6.7l2.1-2.1"
          />
        </svg>
      }
      @case ('results') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M4 3v17h17" />
          <circle cx="9" cy="14" r="1" fill="currentColor" stroke="none" />
          <circle cx="13" cy="9" r="1" fill="currentColor" stroke="none" />
          <circle cx="17" cy="11" r="1" fill="currentColor" stroke="none" />
          <circle cx="11" cy="16" r="1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="6" r="1" fill="currentColor" stroke="none" />
        </svg>
      }
      @case ('discussion') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path
            d="M3.5 10.5C3.5 6.36 7.36 3 12 3s8.5 3.36 8.5 7.5c0 4.14-3.86 7.5-8.5 7.5a9 9 0 0 1-2.3-.3l-4.2 1.8 1.3-3.6A7.2 7.2 0 0 1 3.5 10.5z"
          />
          <circle cx="9" cy="10.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="12" cy="10.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="10.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      }
      @case ('conclusion') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 6l1.5 1.5L7 5" />
          <path d="M3 12l1.5 1.5L7 11" />
          <path d="M3 18l1.5 1.5L7 17" />
          <line x1="10" y1="6" x2="20" y2="6" />
          <line x1="10" y1="12" x2="20" y2="12" />
          <line x1="10" y1="18" x2="18" y2="18" />
        </svg>
      }
      @case ('references') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M4 4h11a4 4 0 0 1 4 4v12H8a4 4 0 0 1-4-4V4z" />
          <path d="M4 4v12a4 4 0 0 1 4-4h11" />
        </svg>
      }
      @case ('acknowledgments') {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />
        </svg>
      }
      @default {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M6 3h12v18l-6-3-6 3z" />
        </svg>
      }
    }
  `,
})
export class PaperSectionIconComponent {
  /** Section name to look up. Trimmed and lowercased before matching. */
  readonly sectionName = input.required<string>();

  /** Resolved glyph identifier — exposed so tests / Storybook can introspect. */
  readonly iconKey = computed<PaperSectionIconKey>(() => resolveIconKey(this.sectionName()));
}
